import { useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Section } from "@/components/details";
import { Module } from "@/constants";
import { formatDateTime } from "@/lib/utils/formatters";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useAgreementContext } from "@/modules/agreement/context";
import { useAgreementData } from "@/modules/agreement/hooks/useAgreementData";
import { useAgreementPaperConfirmation } from "@/modules/agreement/hooks/useAgreementPaperConfirmation";
import {
  canEditPaperConfirmation,
  isPaperConfirmationVisible,
  type PaperConfirmationKind,
} from "@/modules/agreement/permissions/agreementPaperConfirmationAccess";

interface AgreementPaperConfirmationFieldProps {
  /** Rodzaj potwierdzenia: Dział Kadr ("hr", edytowalny) albo strona operacyjna ("recruiter", tylko odczyt) */
  kind: PaperConfirmationKind;
}

/** Pole szczegółów i pola danych dla każdego rodzaju potwierdzenia. */
const FIELD_CONFIG = {
  hr: {
    field: "agreement_hr_paper_confirmed_at",
    confirmedKey: "agreement_hr_paper_confirmed",
    confirmedAtKey: "agreement_hr_paper_confirmed_at",
    confirmedByNameKey: "agreement_hr_paper_confirmed_by_name",
  },
  recruiter: {
    field: "agreement_recruiter_paper_confirmed_at",
    confirmedKey: "agreement_recruiter_paper_confirmed",
    confirmedAtKey: "agreement_recruiter_paper_confirmed_at",
    confirmedByNameKey: "agreement_recruiter_paper_confirmed_by_name",
  },
} as const;

/**
 * Checkbox potwierdzenia otrzymania umowy papierowej (ASMMNT-83) w sekcji „Szczegóły umowy".
 * Widoczny tylko dla umów obcokrajowców. Wariant `hr` zapisuje natychmiast (z uprawnieniem);
 * wariant `recruiter` jest od etapu 2 tylko do odczytu — TAK/NIE i „kto, kiedy" wynikają
 * z aktywnych skanów w sekcji „Załączniki". Musi być renderowany wewnątrz
 * `<Section module={Module.Agreements}>`.
 */
export const AgreementPaperConfirmationField = ({
  kind,
}: AgreementPaperConfirmationFieldProps) => {
  const { t } = useTranslation();
  // Identyfikator z trasy: ten sam, który jest kluczem cache szczegółów aktualizowanego przez hook.
  const { agreementId } = useAgreementContext();
  const { agreement, non_draft } = useAgreementData();
  const permissions = useModulePermissions(Module.Agreements);
  const mutation = useAgreementPaperConfirmation(agreementId);
  const config = FIELD_CONFIG[kind];
  const hintId = useId();
  const [isHintOpen, setIsHintOpen] = useState(false);

  const isConfirmed = agreement[config.confirmedKey] === true;
  const confirmedAt = agreement[config.confirmedAtKey];
  const confirmedByName = agreement[config.confirmedByNameKey];
  const isEditable =
    kind === "hr" &&
    canEditPaperConfirmation({
      hasPermission: permissions.agreements_paper_confirm_hr === true,
      citizenshipName: non_draft.citizenship_name,
    });

  const handleCheckedChange = useCallback(
    (checked: boolean | "indeterminate") => {
      // Tylko Dział Kadr zapisuje ręcznie; hook przyjmuje wyłącznie `kind: "hr"`.
      if (kind !== "hr") return;
      mutation.mutate({ kind, confirmed: checked === true });
    },
    [kind, mutation],
  );

  // Klik (także dotyk) przełącza podpowiedź; Radix zamyka tooltip przy pointerdown, więc klik
  // po najechaniu zostawia go otwartego, a tapnięcie na telefonie go otwiera.
  const toggleHint = useCallback(() => setIsHintOpen((open) => !open), []);

  if (!isPaperConfirmationVisible(non_draft.citizenship_name)) {
    return null;
  }

  // CONCAT_WS w widoku daje pusty string, gdy nie ma potwierdzającego.
  const confirmedByText =
    isConfirmed && confirmedAt && confirmedByName
      ? t("agreements.paperConfirmation.confirmedBy", {
          name: confirmedByName,
          date: formatDateTime(confirmedAt),
        })
      : null;

  return (
    <Section.Field
      field={config.field}
      value={
        <div className="flex flex-col gap-1">
          {/* Tekst „kto, kiedy" celowo poza <label>: klik w nazwisko nie może cofać potwierdzenia. */}
          <div className="flex items-center gap-2">
            <Checkbox
              aria-label={t(`agreements.fields.${config.field}`)}
              aria-describedby={kind === "recruiter" ? hintId : undefined}
              checked={isConfirmed}
              disabled={!isEditable || mutation.isPending}
              onCheckedChange={handleCheckedChange}
            />
            {kind === "recruiter" && (
              // Podpowiedź jako tooltip przy ikonie, nie jako tekst obok (rozjeżdżał układ pola).
              // Ikona jest przyciskiem: tooltip otwiera się z klawiatury (fokus) i dotykiem (klik);
              // czytnik ekranu dostaje tę samą treść przez aria-describedby checkboxa (span sr-only).
              <TooltipProvider delayDuration={200}>
                <Tooltip open={isHintOpen} onOpenChange={setIsHintOpen}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={t(
                        "agreements.paperConfirmation.recruiterInfo",
                      )}
                      onClick={toggleHint}
                    >
                      <Info className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    align="start"
                    className="max-w-xs whitespace-normal text-xs"
                  >
                    {t("agreements.paperConfirmation.recruiterFromScans")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {confirmedByText && (
              <span className="text-xs text-gray-600">{confirmedByText}</span>
            )}
          </div>
          {kind === "recruiter" && (
            <span id={hintId} className="sr-only">
              {t("agreements.paperConfirmation.recruiterFromScans")}
            </span>
          )}
        </div>
      }
    />
  );
};
